import { useEffect, useState } from 'react';
import { useServiceWorker } from './service-worker-provider';
import { NewVersionALert } from 'src/components/new-version-alert';
import toast from 'react-hot-toast';
import { useNewVersionStore } from 'src/shared/stores/useNewVersionToasterStore';
import { useBugsnag } from './bugsnag-provider';

const isServiceWorkerFetchTransientError = (error: unknown) => {
  if (!(error instanceof Error)) {
    return false;
  }

  const normalizedMessage = error.message.toLowerCase();

  return (
    normalizedMessage.includes('failed to update a serviceworker') &&
    normalizedMessage.includes('fetching the script')
  );
};

export function NewVersionToasterProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [shouldShowToaster, setShouldShowToaster] = useState(false);
  const [isLoadingDispatch, setIsLoadingDispatch] = useState(false);
  const {
    hasPendingSuccessfullyUpdatedToaster,
    setHasPendingSuccessfullyUpdatedToaster,
  } = useNewVersionStore();
  const bugsnag = useBugsnag();

  const { assetsUpdateReady, updateAssets, waitingServiceWorker } =
    useServiceWorker();

  useEffect(() => {
    const checkForUpdates = async () => {
      try {
        const registrations = await navigator.serviceWorker.getRegistrations();
        const hasSomeWaiting = registrations.some(
          (registration) => !!registration.waiting,
        );

        if (!hasSomeWaiting && navigator.serviceWorker) {
          const registration = await navigator.serviceWorker.ready;

          if (
            !registration.waiting &&
            !isLoadingDispatch &&
            !hasPendingSuccessfullyUpdatedToaster &&
            navigator.onLine
          ) {
            await registration.update();
          }
        }
      } catch (error) {
        if (isServiceWorkerFetchTransientError(error)) {
          console.warn(
            '[ServiceWorker] Atualizacao ignorada por erro transiente ao buscar service-worker.js',
            error,
          );
          return;
        }

        bugsnag?.notify(error as Error);
      }
    };

    const intervalId = setInterval(() => {
      void checkForUpdates();
    }, 300000);

    return () => clearInterval(intervalId);
  }, [bugsnag, isLoadingDispatch, hasPendingSuccessfullyUpdatedToaster]);

  useEffect(() => {
    const checkForWaitingServiceWorker = async () => {
      try {
        const registrations = await navigator.serviceWorker.getRegistrations();

        registrations.forEach((registration) => {
          if (registration.waiting) {
            registration.waiting.addEventListener('statechange', (event) => {
              const worker = event.target as ServiceWorker;
              if (worker.state === 'activated') {
                setHasPendingSuccessfullyUpdatedToaster(true);
                window.location.reload();
              }
            });

            registration.waiting.postMessage({ type: 'SKIP_WAITING' });
          }
        });
      } catch (error) {
        bugsnag?.notify(error as Error);
      }
    };

    void checkForWaitingServiceWorker();
  }, [bugsnag, setHasPendingSuccessfullyUpdatedToaster]);

  useEffect(() => {
    if (hasPendingSuccessfullyUpdatedToaster) {
      const intervalId = setTimeout(() => {
        toast.success('Sistema atualizado com sucesso!');
      }, 2000);
      setHasPendingSuccessfullyUpdatedToaster(false);
      return () => clearInterval(intervalId);
    }
  }, []);

  useEffect(() => {
    if (assetsUpdateReady && waitingServiceWorker) {
      setShouldShowToaster(true);
    }
  }, [assetsUpdateReady, waitingServiceWorker]);

  return (
    <>
      <NewVersionALert
        show={shouldShowToaster}
        isLoading={isLoadingDispatch}
        handleUpdateAssets={() => {
          setIsLoadingDispatch(true);
          updateAssets()
            .catch((error) => {
              bugsnag?.notify(error);
              window.location.reload();
            })
            .finally(() => {
              setIsLoadingDispatch(false);
            });
        }}
      />
      {children}
    </>
  );
}
